import { Popover, Tag } from "antd"
import { CalendarDays, ChevronDown, ChevronUp, Route, SquareArrowOutUpRight, Trash } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Navigation, OBJECT_TOUR_TASKS_HASH } from "../../../api/constants"
import { ReactComponent as ARROWUP } from "../../../assets/ChevronLeft.svg"
import { ReactComponent as ARROWDOWN } from "../../../assets/ChevronRight.svg"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { Tour, TourObjectTask, TourStop, TourWeekday, TOUR_WEEKDAYS, PREVIEW_STATUS_COLOR, PREVIEW_STATUS_LABEL, TASK_STATUS_LABEL } from "../types"
import { weekLabel } from "../data/tourTasks"
import TourDeletePopover from "./TourDeletePopover"

type TourStopCardType = {
    stop: TourStop
    weekTasks?: TourObjectTask[]
    occupiedWeekdays: TourWeekday[]
    otherTours: Tour[]
    canEdit: boolean
    onRemove: () => void
    onMoveUp: () => void
    onMoveDown: () => void
    onChangeWeekday: (weekday: TourWeekday) => void
    onMoveToTour: (tourId: string, weekday: TourWeekday) => void
}

const formatStopAddress = (adress: TourStop["object"]["adress"]) => {
    const streetLine = [adress?.street, adress?.nr].filter(Boolean).join(" ")
    const cityLine = [adress?.plz, adress?.city].filter(Boolean).join(" ")
    return [streetLine, cityLine].filter(Boolean).join(", ")
}

const objectPath = (objectId: string) => `${Navigation.OBJECTS}${objectId}`
const objectTasksPath = (objectId: string) => `${objectPath(objectId)}#${OBJECT_TOUR_TASKS_HASH}`

const taskPriority = (status: TourObjectTask["status"]) => (status === "open" || status === "rejected" ? 0 : 1)

const TASK_STATUS_DOT: Record<TourObjectTask["status"], string> = {
    open: "bg-amber-400",
    done: "bg-green-500",
    rejected: "bg-red-500",
    dismissed: "bg-gray-300",
}

const iconBtn = "btn btn-ghost btn-xs px-1 min-h-0 h-7"

export default function TourStopCard(props: TourStopCardType) {
    const address = formatStopAddress(props.stop.object.adress)
    const title = props.stop.object.objectIndication || address
    const objectId = props.stop.object._id
    const assignedTaskId = props.stop.adHocTask?.taskId
    const assignedTask = (props.weekTasks ?? []).find((task) => assignedTaskId && task._id === assignedTaskId)
    const tasks = [...(props.weekTasks ?? [])]
        .filter((task) => !assignedTaskId || task._id !== assignedTaskId)
        .sort((a, b) => taskPriority(a.status) - taskPriority(b.status) || a.activity.localeCompare(b.activity))
    const [tasksCollapsed, setTasksCollapsed] = useState(Boolean(props.stop.isAdHoc))
    const [showAllTasks, setShowAllTasks] = useState(false)
    const visibleTasks = tasksCollapsed ? [] : showAllTasks ? tasks : tasks.slice(0, 3)
    const extraCount = tasksCollapsed || showAllTasks ? 0 : tasks.length - visibleTasks.length
    const canToggleTasks = tasks.length > 0

    return (
        <div className="bg-white rounded-default shadow-lg p-3 flex flex-col gap-2">
            <div className="flex justify-between gap-2 items-start">
                <div className="min-w-0 grow">
                    {objectId ? (
                        <Link to={objectPath(objectId)} className="font-semibold text-sm text-gray-800 hover:text-primary line-clamp-2" title={title}>
                            {title}
                        </Link>
                    ) : (
                        <p className="font-semibold text-sm line-clamp-2" title={title}>
                            {title}
                        </p>
                    )}
                    {address && title !== address && <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{address}</p>}
                </div>
                {props.canEdit && (
                    <Authorized allOf={[P.termine_management]}>
                        <div className="flex items-center gap-0.5 shrink-0">
                            <Popover
                                trigger="click"
                                placement="bottomRight"
                                title="Wochentag"
                                content={
                                    <div className="flex flex-wrap gap-1 max-w-[12rem]">
                                        {TOUR_WEEKDAYS.map((day) => {
                                            const occupied = props.occupiedWeekdays.includes(day.value)
                                            const selected = props.stop.weekday === day.value
                                            return (
                                                <button
                                                    key={day.value}
                                                    type="button"
                                                    disabled={occupied && !selected}
                                                    title={occupied && !selected ? "Objekt ist an diesem Tag bereits zugeordnet" : day.label}
                                                    className={`btn btn-xs ${selected ? "btn-primary" : "btn-ghost bg-gray-100"} disabled:opacity-40`}
                                                    onClick={() => {
                                                        if (!selected && !occupied) props.onChangeWeekday(day.value)
                                                    }}>
                                                    {day.short}
                                                </button>
                                            )
                                        })}
                                    </div>
                                }>
                                <button type="button" className={iconBtn} title="Wochentag ändern">
                                    <CalendarDays className="w-4 h-4 text-gray-400" />
                                </button>
                            </Popover>
                            {props.otherTours.length > 0 && (
                                <Popover
                                    trigger="click"
                                    placement="bottomRight"
                                    title="Andere Tour"
                                    content={
                                        <div className="flex flex-col min-w-[10rem]">
                                            {props.otherTours.map((tour) => (
                                                <button
                                                    key={tour._id}
                                                    type="button"
                                                    className="btn btn-ghost btn-sm justify-start font-normal"
                                                    onClick={() => props.onMoveToTour(tour._id, props.stop.weekday)}>
                                                    {tour.name}
                                                </button>
                                            ))}
                                        </div>
                                    }>
                                    <button type="button" className={iconBtn} title="Auf andere Tour verschieben">
                                        <Route className="w-4 h-4 text-gray-400" />
                                    </button>
                                </Popover>
                            )}
                            <TourDeletePopover
                                text="Wollen Sie dieses Objekt wirklich von der Tour entfernen?"
                                onConfirm={props.onRemove}
                                iconClassName="w-5 h-5 shrink-0 cursor-pointer hover:text-error">
                                <Trash className="w-4 h-4 shrink-0 cursor-pointer hover:text-error text-gray-400" />
                            </TourDeletePopover>
                        </div>
                    </Authorized>
                )}
            </div>
            <div className="flex flex-wrap gap-1">
                <Tag className="m-0" color={PREVIEW_STATUS_COLOR[props.stop.previewStatus]}>
                    {PREVIEW_STATUS_LABEL[props.stop.previewStatus]}
                </Tag>
                {props.stop.isAdHoc && (
                    <Tag className="m-0" color="purple">
                        Außerplanmäßig
                    </Tag>
                )}
            </div>
            {props.stop.adHocTask && (
                <div className="rounded-default bg-purple-50 px-2 py-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-400">
                        {props.stop.adHocTask.kind === "openTask" ? "Zugeordnete Aufgabe" : "Einzelaufgabe"}
                    </p>
                    <p className="text-xs text-gray-700 mt-0.5">{props.stop.adHocTask.activity}</p>
                    {assignedTask?.rejectionComment && <p className="text-[11px] text-gray-500 mt-0.5">{assignedTask.rejectionComment}</p>}
                </div>
            )}
            <div className="flex flex-col gap-1.5 pt-1 border-t">
                <div className="flex items-start justify-between gap-2">
                    {canToggleTasks ? (
                        <button
                            type="button"
                            className="flex items-center gap-1 min-w-0 text-left"
                            onClick={() => setTasksCollapsed((collapsed) => !collapsed)}
                            aria-expanded={!tasksCollapsed}
                            title={tasksCollapsed ? "Aufgaben einblenden" : "Aufgaben ausblenden"}>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {tasks.length === 1 ? "1 Aufgabe diese Woche" : `${tasks.length} Aufgaben diese Woche`}
                            </p>
                            {tasksCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                        </button>
                    ) : (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Keine Aufgaben diese Woche</p>
                    )}
                    {objectId && (
                        <Link to={objectTasksPath(objectId)} title="Aufgaben am Objekt" className="shrink-0 text-gray-300 hover:text-gray-500">
                            <SquareArrowOutUpRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
                {visibleTasks.map((task) => (
                    <div key={task._id} className="min-w-0">
                        <p className="text-xs font-bold text-gray-800 line-clamp-2" title={task.activity}>
                            {task.activity}
                        </p>
                        {task.description && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{task.description}</p>}
                        {task.rejectionComment && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{task.rejectionComment}</p>}
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                            <span className="text-[11px] text-gray-400">{weekLabel(task)}</span>
                            <span
                                className={`w-2 h-2 rounded-full shrink-0 ${TASK_STATUS_DOT[task.status]}`}
                                title={TASK_STATUS_LABEL[task.status]}
                                aria-label={TASK_STATUS_LABEL[task.status]}
                            />
                        </div>
                    </div>
                ))}
                {extraCount > 0 && (
                    <button type="button" className="text-[11px] text-gray-400 hover:text-gray-600 text-left" onClick={() => setShowAllTasks(true)}>
                        +{extraCount} weitere
                    </button>
                )}
                {showAllTasks && !tasksCollapsed && tasks.length > 3 && (
                    <button type="button" className="text-[11px] text-gray-400 hover:text-gray-600 text-left" onClick={() => setShowAllTasks(false)}>
                        weniger anzeigen
                    </button>
                )}
            </div>
            {props.canEdit && (
                <Authorized allOf={[P.termine_management]}>
                    <div className="flex items-center justify-center gap-1 pt-1">
                        <button type="button" className={iconBtn} title="Nach oben" onClick={props.onMoveUp}>
                            <ARROWUP className="w-4 h-4 rotate-90" />
                        </button>
                        <button type="button" className={iconBtn} title="Nach unten" onClick={props.onMoveDown}>
                            <ARROWDOWN className="w-4 h-4 rotate-90" />
                        </button>
                    </div>
                </Authorized>
            )}
        </div>
    )
}
